/**
 * Pathway Registry
 * Central location to register all pathways
 * When you add a new pathway, export it here and add to the map
 */

import type { Pathway } from "../types";
import { utiPathway } from "./uti.pathway";
import { shinglesPathway } from "./shingles.pathway";
import { insectBitesPathway } from "./insectbites.pathway";
import { soreThroatPathway } from "./sorethroat.pathway";
import { impetigoPathway } from "./impetigo.pathway";
import { otitisMediaPathway } from "./otitismedia.pathway";
import { sinusitisPathway } from "./sinusitis.pathway";
import { ahtWeightSelectionPathway } from "./aht/weight-selection.pathway";
import { ahtWtSelectorPathway } from "./aht/aht-wt-selector.pathway";
import { ahtWtReinitiationDrugSelectPathway } from "./aht/aht-wt-reinitiation-drug-select.pathway";
import { ahtWtTirzepatideInitialPathway } from "./aht/aht-wt-tirzepatide-initial.pathway";
import { ahtWtSemaglutideInitialPathway } from "./aht/aht-wt-semaglutide-initial.pathway";
import { ahtWtOralSemaglutideInitialPathway } from "./aht/aht-wt-oral-semaglutide-initial.pathway";
import { ahtWtLiraglutideInitialPathway } from "./aht/aht-wt-liraglutide-initial.pathway";
import { ahtWtMysimbaInitialPathway } from "./aht/aht-wt-mysimba-initial.pathway";
import { ahtWtOrlistatInitialPathway } from "./aht/aht-wt-orlistat-initial.pathway";
import { ahtWtTirzepatideRescreenPathway } from "./aht/aht-wt-tirzepatide-rescreen.pathway";
import { ahtWtSemaglutideRescreenPathway } from "./aht/aht-wt-semaglutide-rescreen.pathway";
import { ahtWtLiraglutideRescreenPathway } from "./aht/aht-wt-liraglutide-rescreen.pathway";
import { ahtWtTirzepatideTransferPathway } from "./aht/aht-wt-tirzepatide-transfer.pathway";
import { ahtWtSemaglutideTransferPathway } from "./aht/aht-wt-semaglutide-transfer.pathway";
import { ahtWtLiraglutideTransferPathway } from "./aht/aht-wt-liraglutide-transfer.pathway";
import { ahtWtMysimbaTransferPathway } from "./aht/aht-wt-mysimba-transfer.pathway";
import { ahtWtOrlistatTransferPathway } from "./aht/aht-wt-orlistat-transfer.pathway";
import { ahtWtSharedAssessmentPathway } from "./aht/aht-wt-shared-assessment.pathway";

/**
 * Map of pathwayId → Pathway config
 * The engine loads a pathway by ID from here
 */
export const pathwayRegistry: Record<string, Pathway> = {
  uti: utiPathway,
  shingles: shinglesPathway,
  insectbites: insectBitesPathway,
  sorethroat: soreThroatPathway,
  impetigo: impetigoPathway,
  otitismedia: otitisMediaPathway,
  sinusitis: sinusitisPathway,
  "aht-weight-selection": ahtWeightSelectionPathway,
  "aht-wt-selector": ahtWtSelectorPathway,
  "aht-wt-reinitiation-drug-select": ahtWtReinitiationDrugSelectPathway,
  "aht-wt-tirzepatide-initial": ahtWtTirzepatideInitialPathway,
  "aht-wt-semaglutide-initial": ahtWtSemaglutideInitialPathway,
  "aht-wt-oral-semaglutide-initial": ahtWtOralSemaglutideInitialPathway,
  "aht-wt-liraglutide-initial": ahtWtLiraglutideInitialPathway,
  "aht-wt-mysimba-initial": ahtWtMysimbaInitialPathway,
  "aht-wt-orlistat-initial": ahtWtOrlistatInitialPathway,
  "aht-wt-tirzepatide-rescreen": ahtWtTirzepatideRescreenPathway,
  "aht-wt-semaglutide-rescreen": ahtWtSemaglutideRescreenPathway,
  "aht-wt-liraglutide-rescreen": ahtWtLiraglutideRescreenPathway,
  "aht-wt-tirzepatide-transfer": ahtWtTirzepatideTransferPathway,
  "aht-wt-semaglutide-transfer": ahtWtSemaglutideTransferPathway,
  "aht-wt-liraglutide-transfer": ahtWtLiraglutideTransferPathway,
  "aht-wt-mysimba-transfer":     ahtWtMysimbaTransferPathway,
  "aht-wt-orlistat-transfer":    ahtWtOrlistatTransferPathway,
  "aht-wt-shared-assessment":    ahtWtSharedAssessmentPathway,
};

/**
 * Helper to look up a pathway by ID
 * Throws if not found (should never happen in production)
 */
export function getPathway(pathwayId: string): Pathway {
  const pathway = pathwayRegistry[pathwayId];
  if (!pathway) {
    throw new Error(
      `Pathway not found: ${pathwayId}. Register it in pathwayRegistry before use.`
    );
  }
  return pathway;
}

/**
 * Get all available pathways
 */
export function getAvailablePathways(): Pathway[] {
  return Object.values(pathwayRegistry);
}
